import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { Input, RadioGroup, Radio } from "@citi-icg-172888/icgds-react";
import cx from "classnames";
import { ICompanyInfo } from "../../services/types/LeadRequisitionType";
import SelectHoc from "../../common/SelectHoc";
import { getSelectedValue } from "../../services/utils/utils";
import { useData } from "../../services/context/DataContext";

interface CompanyDetailsProps {
  className: string;
  companyInfo: ICompanyInfo | null;
  onChange: (data: ICompanyInfo, field: string) => void;
  errorSalesSize: string;
  errorIndustryType: string;
  errorVcPe: string;
  errorWebSite: string;
}

const CompanyDetails: React.FC<CompanyDetailsProps> = ({
  className,
  companyInfo,
  onChange,
  errorSalesSize,
  errorIndustryType,
  errorVcPe,
  errorWebSite,
}) => {
  const { staticData } = useData();
  const { Industries, SalesSize } = getSelectedValue(companyInfo, staticData);
  const [hasExecuted, setHasExecuted] = useState(false);

  useEffect(() => {
    if (companyInfo?.salesSize === "Pre-Revenue") {
      const radioGroup = document.querySelectorAll(".lmn-selection-control");

      radioGroup.forEach((marker) => {
        marker.setAttribute("tabindex", "0");
        marker.addEventListener("keydown", (event: any) => {
          const options = ["Yes", "No"];
          const currentIndex = options.indexOf(event.target.textContent);
          if (event.key === "Enter" && event.target.textContent) {
            const mockEvent = {
              target: {
                name: "isCompanyVCorPEBacked",
                value: event.target.textContent, // Use the textContent as the value
              },
            } as ChangeEvent<HTMLInputElement>;
            onChangeHandler(mockEvent);
          } else if (event.key === "ArrowUp" && event.target.textContent) {
            const prevIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
            const prevMarker = radioGroup[prevIndex] as HTMLInputElement;
            if (prevMarker) {
              prevMarker.focus();
            }
            const mockEvent = {
              target: {
                name: "isCompanyVCorPEBacked",
                value: options[prevIndex], // Use the textContent as the value
              },
            } as ChangeEvent<HTMLInputElement>;
            onChangeHandler(mockEvent);
          } else if (event.key === "ArrowDown" && event.target.textContent) {
            const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : currentIndex;
            const nextMarker = radioGroup[nextIndex] as HTMLInputElement;
            if (nextMarker) {
              nextMarker.focus();
            }
            const mockEvent = {
              target: {
                value: options[nextIndex], // Use the textContent as the value
              },
            } as ChangeEvent<HTMLInputElement>;
            onChangeHandler(mockEvent);
          }
        });
      });
    }
  }, [companyInfo?.salesSize === "Pre-Revenue"]);

  useEffect(() => {
    if (Industries.length === 1 && !hasExecuted) {
      const updatedCompanyInfo: any = {
        ...companyInfo,
        ["industry"]: Industries[0],
      };
      updatedCompanyInfo.isCompanyVCorPEBacked = "";
      onChange(updatedCompanyInfo, "industry");
      setHasExecuted(true); // Set the flag to true to prevent re-execution
    }
  }, [Industries.length, hasExecuted]); // Track changes to Industries.length and hasExecuted

  const onChangeHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      const updatedCompanyInfo: any = {
        ...companyInfo,
        [name]: value,
      };

      onChange(updatedCompanyInfo, name);
    },
    [companyInfo, onChange]
  );

  const onChangeDdlHandler = useCallback(
    (options: string[], ddName: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updatedCompanyInfo: any = {
        ...companyInfo,
        [ddName]: options,
      };
      if (ddName === "industry") {
        updatedCompanyInfo.salesSize = "";
        updatedCompanyInfo.isCompanyVCorPEBacked = "";
      }
      onChange(updatedCompanyInfo, ddName);
    },
    [companyInfo, onChange]
  );

  return (
    <div className={cx("lmn-my-5 lmn-dnca-w-m-60 lmn-dnca-w-l-60 lmn-dnca-w-xl-60", className)}>
      <h4 className="lmn-text-color" role="heading" aria-level={4}>
        Additional Details
      </h4>
      <div className="">
        <div className="lmn-form-group lmn-mb-5">
          <label className="lmn-required" htmlFor="industry_type">Industry Type</label>
          <SelectHoc
            id="industry_type"
            value={Industries.length > 1 ? companyInfo?.industry : Industries[0]}
            label="Industry Type"
            options={Industries || []}
            error={errorIndustryType}
            onChange={(options: any) => onChangeDdlHandler(options, "industry")}
          />
          {errorIndustryType && <small>{errorIndustryType}</small>}
          <span className="lmn-hint-text-color lmn-font-size-12" role="tooltip" id="industry_type_hint">
            If you don't see your industry, we may not be able to help.
          </span>
        </div>
        <div className="lmn-form-group lmn-mb-5" >
          <label className="lmn-required" htmlFor="website">Company Website</label>
          <Input
            name="website"
            id="company_website"
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            invalid={!!errorWebSite}
            aria-describedby="company_error"  
            aria-label="Company Website"
            value={companyInfo?.website}
            aria-required={true}
            onChange={onChangeHandler}
          />
          {errorWebSite && <div aria-live="assertive" role="alert" id="company_error">
            <span className="visually-hidden">Company Website - </span>
            <small>{errorWebSite}</small> </div>}
        </div>
      </div>
      <div className="">
        <div className="lmn-form-group lmn-mb-5">
          <label className="lmn-required">Annual Revenue</label>
          <SelectHoc
            id="sales_size"
            label="Annual Revenue"
            error={errorSalesSize}
            value={companyInfo?.salesSize}
            options={SalesSize || []}
            onChange={(options: any) => onChangeDdlHandler(options, "salesSize")}
          />
          {errorSalesSize && <small>{errorSalesSize}</small>}
        </div>
        {companyInfo?.salesSize === "Pre-Revenue" && (
          <div className="lmn-form-group">
            <label className="lmn-required" id={`${"groupId"}-label`}>Is your company VC/PE backed?</label>
            <RadioGroup
              name="isCompanyVCorPEBacked"
              value={companyInfo?.isCompanyVCorPEBacked}
              onChange={onChangeHandler}
              aria-label="Is your company VC/PE backed?"
              aria-required={true}
              aria-invalid={!!errorVcPe}
              aria-describedby={errorVcPe ? `${"groupId"}-error` : null}
            >
              <Radio inline id={`${"groupId"}-yes`} value="Yes" aria-label="Is your company VC/PE backed? Yes" tabIndex={0}>
                Yes
              </Radio>
              <Radio inline id={`${"groupId"}-no`} value="No" aria-label="Is your company VC/PE backed? No" tabIndex={0}>
                No
              </Radio>
            </RadioGroup>
            {errorVcPe && <small id={`${"groupId"}-error`} role="alert">{errorVcPe}</small>}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDetails;
